import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Box, FormControl, FormHelperText, InputLabel, MenuItem, SxProps } from "@mui/material";
import { IDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { DataTypes } from "../../rule-engine-core/Common/Data/DataTypes";
import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import { EditGrayIcon } from "../Icon/EditGrayIcon";

import { DataDefEditLayer } from "../DataDesigner/DataDefEditLayer";
import { StyledSelect } from "../BuildlingBlocks/StyledSelect";
import { UILabel } from "../BuildlingBlocks/UILabel";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

export interface DataTypePickerProps {
    dataDef: IDataDefinition;
    updateCallback: (type: IDataDefinition) => void;
    label?: string;
    hint?: string;
    error?: string;
    readonly?: boolean;
    width?: string;
    variant?: "minimal" | "header" | "outlined" | undefined;
    sx?: SxProps;
    sxInput?: SxProps;
}

export function DataTypePicker(props: DataTypePickerProps) {
    const [dataDef, setDataDef] = useState<IDataDefinition>(props.dataDef);
    const ref = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState<boolean>(false);

    const onEditProperty = () => {
        setOpen(true);
    };

    useEffect(() => {
        if (dataDef.type === DataTypes.OBJECT && (dataDef.fields?.length || 0) === 0) {
            setOpen(true);
        }
    }, [dataDef]);

    let sxInputVal: SxProps = props.sxInput ?? {};
    if (props.variant === "minimal")
        sxInputVal = {
            fontSize: 12,
            boxShadow: "none",
            ".MuiOutlinedInput-notchedOutline": { border: 0 },
            ...props.sxInput,
        };

    return (
        <Box
            display={"flex"}
            alignItems={"center"}
            data-testid="DataTypePicker"
            sx={props.sx}
        >
            {props.variant === "header" && <UILabel paddingRight={"10px"}>{props.label}</UILabel>}
            <FormControl fullWidth={true}>
                {props.variant !== "header" && (
                    <InputLabel sx={{ paddingLeft: "3px", paddingRight: "3px", background: "white" }}>
                        {props.label}
                    </InputLabel>
                )}
                <StyledSelect
                    value={dataDef.type}
                    onChange={(event) => {
                        let newType = DataUtilities.createDataByType(
                            event.target.value as DataTypes,
                            props.dataDef.name,
                        );
                        props.updateCallback(newType);
                        setDataDef(newType);
                    }}
                    disabled={props.readonly}
                    style={{ width: props.width }}
                    size={"small"}
                    sx={sxInputVal}
                    error={Boolean(props.error)}
                    MenuProps={{
                        style: {
                            paddingRight: 5,
                            paddingLeft: 5,
                        },
                    }}
                >
                    <StyledMenuItem value={DataTypes.STRING}>{DataTypes.STRING}</StyledMenuItem>
                    <StyledMenuItem value={DataTypes.BOOLEAN}>{DataTypes.BOOLEAN}</StyledMenuItem>
                    <StyledMenuItem value={DataTypes.NUMBER}>{DataTypes.NUMBER}</StyledMenuItem>
                    <StyledMenuItem value={DataTypes.DATE}>{DataTypes.DATE}</StyledMenuItem>
                    <StyledMenuItem value={DataTypes.FILE}>{DataTypes.FILE}</StyledMenuItem>
                    <StyledMenuItem value={DataTypes.OBJECT}>{DataTypes.OBJECT}</StyledMenuItem>
                </StyledSelect>
                {(props.hint || props.error) && (
                    <FormHelperText sx={{ color: props.error ? UIStyleConstants.RED_COLOR : undefined }}>
                        {props.error ?? props.hint}
                    </FormHelperText>
                )}
            </FormControl>
            {dataDef.type !== DataTypes.BOOLEAN && dataDef.type !== DataTypes.FILE && (
                <Box
                    marginTop={props.hint ? "-15px" : undefined}
                    marginLeft={props.variant === "minimal" ? "-12px" : undefined}
                >
                    <EditPropertyButton
                        onClick={onEditProperty}
                        className="DataTypePicker-edit"
                        data-testid="DataTypePicker-edit"
                        ref={ref}
                    >
                        <EditGrayIcon />
                    </EditPropertyButton>
                </Box>
            )}

            <DataDefEditLayer
                dataDef={dataDef}
                onUpdate={() => {
                    props.updateCallback(dataDef);
                }}
                open={open}
                onClose={() => {
                    setOpen(false);
                }}
            />
        </Box>
    );
}

const EditPropertyButton = styled.div<{ hide?: boolean }>`
    width: 16px;
    height: 16px;
    cursor: pointer;

    margin-left: 10px;

    ${(props) => (props.hide ? "display: none;" : "display: block;")}
`;

const StyledMenuItem = styled(MenuItem)`
    && {
        padding: 8px 16px !important;

        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0px;
        text-align: left;
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
    }
`;
